import { useMemo, useState } from "react";
import { SectionCard } from "shared/app/SectionCard";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  Show
} from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
import { useFormTyped } from "shared/hooks";
import { Row } from "views/applications-id-report/components/Row";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { Unit } from "types/landlord/address";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import { instance } from "api/apiBase";
import { useAuthContext } from "providers/Auth";
import { PmsSyncOption } from "shared/app/RentalInfoForm/components/PmsSyncOption";
import { PMSValue } from "shared/app/RentalInfoForm/components/PmsValue";
import { useEditDetailsPermissions } from "shared/hooks/use-edit-details-permissions";
import {
  buildMarketingInfoPayload,
  buildMarketingInfoSchema,
  SMOKING_POLICY_LABEL,
  SMOKING_POLICY_MAX_LENGTH,
  type ListField,
  type MarketingInfoFormValues as FormValues
} from "./marketing-info-form";

// CRM-1167. Both fields are free-text label lists the PMS owns (Rent Manager
// spells them "12 Months", "Sewage"), so the editor is a comma-separated line
// rather than a fixed option list — a hard-coded set would silently drop
// whatever wording a partner actually uses.
const SECTION_TITLE = "Lease terms, utilities & policies";

const toInput = (list: string[] | null | undefined): string =>
  (list ?? []).join(", ");

const ListValue = ({ items }: { items: string[] | null | undefined }) => {
  if (!items?.length) return <p className="font-medium text-content-1000">-</p>;

  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => (
        <Badge key={item} variant={BadgeVariant.BASE} size={BadgeSize.SM}>
          {item}
        </Badge>
      ))}
    </div>
  );
};

const FIELDS: { key: ListField; label: string; placeholder: string }[] = [
  {
    key: "lease_terms",
    label: "Lease terms",
    placeholder: "6 Months, 12 Months"
  },
  {
    key: "included_utilities",
    label: "Included utilities",
    placeholder: "Water, Sewage"
  }
];

const EditMarketingInfoForm = ({
  onClose,
  initialValues,
  unit,
  isPmsUnit
}: {
  onClose: () => void;
  initialValues: FormValues;
  unit: Unit;
  isPmsUnit: boolean;
}) => {
  const queryClient = useQueryClient();
  const schema = useMemo(
    () => buildMarketingInfoSchema(isPmsUnit),
    [isPmsUnit]
  );
  const { register, onSubmit, values } = useFormTyped(initialValues, schema);
  const overrides = values.pms_overrides;

  const { mutate: save, isPending } = useMutation({
    mutationFn: () =>
      instance.patch(
        `/portal/v1/units/${unit.id}`,
        buildMarketingInfoPayload(values, unit, isPmsUnit)
      ),
    onSuccess: () => {
      toast.success(`${SECTION_TITLE} updated successfully`);
      queryClient.invalidateQueries({
        queryKey: ["unit"]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(
        `Failed to update ${SECTION_TITLE}: ${getErrorMessage(error)}`
      );
    }
  });

  const handleSubmit = onSubmit({ onSuccess: () => save() });

  // Switching sync back ON mirrors the PMS list into the field — including the
  // empty case — so the form never shows a manual value that contradicts the
  // "PMS Value" line next to it.
  const handleOverrideChange = (field: ListField, synced: boolean) => {
    register("pms_overrides").onChange({ ...overrides, [field]: synced });
    if (synced) register(field).onChange(toInput(unit.pms_values?.[field]));
  };

  const handleSmokingOverrideChange = (synced: boolean) => {
    register("pms_overrides").onChange({
      ...overrides,
      smoking_policy: synced
    });
    if (synced)
      register("smoking_policy").onChange(
        unit.pms_values?.smoking_policy ?? ""
      );
  };

  const smokingSynced = overrides?.smoking_policy ?? true;

  return (
    <SectionCard
      title={SECTION_TITLE}
      footer={
        <SaveCancelButtons
          onSave={(e) => handleSubmit(e)}
          onCancel={onClose}
          saveDisabled={isPending}
          containerClassName="flex gap-2 pt-6"
        />
      }
    >
      <div className="space-y-8">
        {FIELDS.map(({ key, label, placeholder }) => {
          const synced = overrides?.[key] ?? true;

          return (
            <div key={key} className="grid grid-cols-2 gap-4 items-start">
              <div>
                <BaseInput
                  id={key}
                  label={label}
                  placeholder={placeholder}
                  value={register(key).value ?? ""}
                  onChange={(val) => register(key).onChange(val)}
                  error={register(key).error}
                  readOnly={isPmsUnit && synced}
                />
                <Show if={isPmsUnit}>
                  <PmsSyncOption
                    value={synced}
                    onChange={(val) => handleOverrideChange(key, val)}
                    name={key}
                  />
                </Show>
              </div>
              <Show if={isPmsUnit}>
                <PMSValue
                  value={
                    unit.pms_values?.[key]?.length
                      ? unit.pms_values[key].join(", ")
                      : undefined
                  }
                />
              </Show>
            </div>
          );
        })}
        <div className="grid grid-cols-2 gap-4 items-start">
          <div>
            <BaseInput
              id="smoking_policy"
              label={SMOKING_POLICY_LABEL}
              placeholder="No smoking"
              value={register("smoking_policy").value ?? ""}
              onChange={(val) => register("smoking_policy").onChange(val)}
              error={register("smoking_policy").error}
              maxLength={SMOKING_POLICY_MAX_LENGTH}
              readOnly={isPmsUnit && smokingSynced}
            />
            <Show if={isPmsUnit}>
              <PmsSyncOption
                value={smokingSynced}
                onChange={handleSmokingOverrideChange}
                name="smoking_policy"
              />
            </Show>
          </div>
          <Show if={isPmsUnit}>
            <PMSValue value={unit.pms_values?.smoking_policy || undefined} />
          </Show>
        </div>
      </div>
    </SectionCard>
  );
};

interface MarketingInfoSectionProps {
  unit: Unit;
}

export const MarketingInfoSection = ({ unit }: MarketingInfoSectionProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const { type } = useAuthContext();
  const { canEditUnitDetails, unitEditDeniedTooltip } =
    useEditDetailsPermissions();
  const isPmsUnit = type.isPmsListingSyncEnabled && !!unit.pms_id;

  if (isEditing) {
    const syncedValue = (field: ListField) =>
      isPmsUnit && (unit.pms_overrides?.[field] ?? true)
        ? toInput(unit.pms_values?.[field])
        : toInput(unit[field]);

    const smokingPolicyValue =
      isPmsUnit && (unit.pms_overrides?.smoking_policy ?? true)
        ? unit.pms_values?.smoking_policy
        : unit.smoking_policy;

    return (
      <EditMarketingInfoForm
        onClose={() => setIsEditing(false)}
        isPmsUnit={isPmsUnit}
        initialValues={{
          lease_terms: syncedValue("lease_terms"),
          included_utilities: syncedValue("included_utilities"),
          smoking_policy: smokingPolicyValue ?? "",
          pms_overrides: {
            lease_terms: unit.pms_overrides?.lease_terms ?? true,
            included_utilities: unit.pms_overrides?.included_utilities ?? true,
            smoking_policy: unit.pms_overrides?.smoking_policy ?? true
          }
        }}
        unit={unit}
      />
    );
  }

  return (
    <SectionCard
      title={SECTION_TITLE}
      headerExtra={
        <Button
          size={ButtonSize.SM}
          variant={ButtonVariant.BASE}
          onClick={() => setIsEditing(true)}
          disabled={!canEditUnitDetails}
          tooltip={unitEditDeniedTooltip}
          tooltipPlacement="bottom-end"
        >
          Edit
        </Button>
      }
    >
      <div className="space-y-4 w-full">
        {FIELDS.map(({ key, label }) => (
          <Row
            key={key}
            title={label}
            value={<ListValue items={unit[key]} />}
            valueClassName="w-[500px]"
          />
        ))}
        <Row
          title={SMOKING_POLICY_LABEL}
          value={
            <p className="font-medium text-content-1000">
              {unit.smoking_policy || "-"}
            </p>
          }
          valueClassName="w-[500px]"
        />
      </div>
    </SectionCard>
  );
};
